import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, Trash2, Mail, ArrowLeft, ShieldCheck, Check, X, AlertTriangle, LogIn, KeyRound, RefreshCw } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api, auth as authLib } from "../lib/api";
import { toast } from "sonner";

/**
 * AdminUsers — one place to manage every account.
 *  • Search + filter (all / subscribers / non-subs / admins)
 *  • Multi-select → send an ad-hoc "note from the desk" email
 *  • Delete test accounts (hard-delete, not recoverable) — admin-guarded
 *  • Row-level: quick email + delete
 */
export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [pendingDelete, setPendingDelete] = useState(null); // { id, email }
  const [composeOpen, setComposeOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/users")
      .then((r) => setUsers(r.data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load users"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return users
      .filter((u) => {
        if (filter === "subscribers" && !u.subscribed) return false;
        if (filter === "non-subs" && u.subscribed) return false;
        if (filter === "admins" && u.role !== "admin") return false;
        if (!term) return true;
        return (
          (u.email || "").toLowerCase().includes(term) ||
          (u.name || "").toLowerCase().includes(term) ||
          (u.id || "").toLowerCase().includes(term)
        );
      })
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "") * -1);
  }, [users, q, filter]);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const toggleAllVisible = () => {
    const visibleIds = filtered.map((u) => u.id);
    const allSelected = visibleIds.every((id) => selected.has(id));
    const next = new Set(selected);
    if (allSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    setSelected(next);
  };

  const sendBulk = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body required.");
      return;
    }
    const ids = Array.from(selected);
    if (!ids.length) {
      toast.error("Select at least one user.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/users/email", {
        user_ids: ids,
        subject: subject.trim(),
        body: body.trim(),
      });
      toast.success(`Sent to ${r.data.sent} of ${r.data.attempted}` + (r.data.failed ? ` · ${r.data.failed} failed` : ""));
      setComposeOpen(false);
      setSubject("");
      setBody("");
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${pendingDelete.id}`);
      toast.success(`Deleted ${pendingDelete.email}`);
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // Impersonation — stash admin session, swap in the target user's token, reload.
  // The banner in App.jsx picks up the stash and gives a one-click return.
  const [impersonatingId, setImpersonatingId] = useState(null);
  const impersonate = async (u) => {
    setImpersonatingId(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/impersonate`);
      authLib.stashAdminSession();
      authLib.setToken(r.data.access_token);
      authLib.setUser(r.data.user);
      toast.success(`Now viewing as ${u.email}`);
      // Full reload so every hook re-fetches with the new token and the
      // /auth/me refresh in AuthContext picks up the impersonated identity.
      window.location.href = "/app";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
      setImpersonatingId(null);
    }
  };

  // Admin-triggered password reset — sends the standard reset email on the
  // target user's behalf. Small helper so a subscriber who can't figure out
  // the forgot-password flow can be helped with a single click.
  const [resettingId, setResettingId] = useState(null);
  const sendReset = async (u) => {
    if (!window.confirm(`Send a password reset link to ${u.email}?`)) return;
    setResettingId(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(`Reset link sent to ${r.data.recipient} — expires in ${r.data.expires_minutes} min`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reset link");
    } finally {
      setResettingId(null);
    }
  };

  // Force-resync a user's subscription state from live Stripe. Handles the
  // "paid in Stripe, still free in Moonbucks" support case (missed / dropped
  // webhook). Hits the existing /admin/users/sync-from-stripe endpoint,
  // then refetches the user list so the badge flips immediately.
  const [syncingId, setSyncingId] = useState(null);
  const syncFromStripe = async (u) => {
    setSyncingId(u.id);
    try {
      const r = await api.post("/admin/users/sync-from-stripe", { email: u.email });
      toast.success(`${u.email} → ${r.data.subscribed ? "subscribed" : "unsubscribed"} (synced from Stripe · key_mode=${r.data.key_mode || "?"})`);
      await load();
    } catch (e) {
      // Surface EVERY signal we have so a bare "Sync failed" never happens
      // again: HTTP status, response detail, response body text, or the
      // network-level error message if the request never reached the API.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const bodyStr = e?.response?.data && typeof e.response.data !== "string"
        ? JSON.stringify(e.response.data)
        : e?.response?.data;
      const netMsg = !e?.response ? (e?.message || "no response — request may have been blocked or timed out") : null;
      const msg = detail || bodyStr || netMsg || "Sync failed (no detail)";
      const banner = status ? `[HTTP ${status}] ${msg}` : `[NET] ${msg}`;
      // Keep the toast on screen long enough to read + let the admin copy it.
      toast.error(banner, { duration: 20000 });
      // eslint-disable-next-line no-console
      console.error("sync-from-stripe failure", { status, detail, body: e?.response?.data, err: e });
    } finally {
      setSyncingId(null);
    }
  };

  const counts = useMemo(() => {
    return {
      total: users.length,
      subs: users.filter((u) => u.subscribed).length,
      admins: users.filter((u) => u.role === "admin").length,
    };
  }, [users]);

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin/dashboard" className="inline-flex items-center gap-1 text-xs text-mb-emerald hover:underline">
              <ArrowLeft className="w-3 h-3" />
              Back to ops
            </Link>
            <h1 className="font-display font-semibold text-4xl tracking-tight mt-1" data-testid="admin-users-title">Users</h1>
            <p className="text-neutral-600 mt-1 text-sm">
              {counts.total} total · <span className="text-mb-emerald font-semibold">{counts.subs} paying</span> · {counts.admins} admin
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setComposeOpen(true)}
              disabled={selected.size === 0}
              className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md"
              data-testid="admin-users-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email selected ({selected.size})
            </Button>
          </div>
        </div>

        {/* Filter + search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
            <Input
              placeholder="Search email, name, or user id…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 rounded-md"
              data-testid="admin-users-search"
            />
          </div>
          <div className="flex gap-1 bg-white border border-mb-border rounded-md p-1">
            {[
              ["all", "All"],
              ["subscribers", "Paying"],
              ["non-subs", "Leads"],
              ["admins", "Admins"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 text-xs rounded ${filter === key ? "bg-mb-emerald text-white font-bold" : "text-neutral-600 hover:bg-neutral-50"}`}
                data-testid={`admin-users-filter-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-mb-emerald" />
          </div>
        ) : (
          <div className="bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="admin-users-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-500 label-uppercase text-[10px]">
                  <tr>
                    <th className="text-left px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        onChange={toggleAllVisible}
                        checked={filtered.length > 0 && filtered.every((u) => selected.has(u.id))}
                        className="accent-mb-emerald"
                        data-testid="admin-users-select-all"
                      />
                    </th>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Plan</th>
                    <th className="text-left px-3 py-2">Joined</th>
                    <th className="text-right px-3 py-2 w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-neutral-500 italic">
                        No users match.
                      </td>
                    </tr>
                  )}
                  {filtered.map((u) => {
                    const isSelected = selected.has(u.id);
                    const isAdmin = u.role === "admin";
                    return (
                      <tr key={u.id} className={`border-t border-mb-border/50 ${isSelected ? "bg-mb-emerald/5" : ""}`} data-testid={`admin-user-row-${u.email}`}>
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOne(u.id)}
                            className="accent-mb-emerald"
                            data-testid={`admin-user-select-${u.email}`}
                          />
                        </td>
                        <td className="px-3 py-2.5 font-mono-data text-xs">{u.email}</td>
                        <td className="px-3 py-2.5">{u.name || <span className="text-neutral-400">—</span>}</td>
                        <td className="px-3 py-2.5">
                          {isAdmin ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono-data font-bold text-mb-emerald bg-mb-emerald/10 px-2 py-0.5 rounded">
                              <ShieldCheck className="w-3 h-3" /> ADMIN
                            </span>
                          ) : u.subscribed ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono-data font-bold text-mb-emerald bg-mb-emerald/10 px-2 py-0.5 rounded">
                              <Check className="w-3 h-3" /> PAYING
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono-data text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded">
                              LEAD
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-neutral-600">
                          {u.subscription_plan ? u.subscription_plan.replace("moonbucks_", "") : "—"}
                          {u.cancel_at_period_end && <span className="ml-1 text-amber-600 font-mono-data text-[10px]">(cancels)</span>}
                          {u.subscription_status === "past_due" && <span className="ml-1 text-red-600 font-mono-data text-[10px]">(past due)</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-neutral-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="inline-flex gap-1">
                            <button
                              onClick={() => impersonate(u)}
                              disabled={isAdmin || impersonatingId === u.id}
                              className={`p-1.5 rounded ${isAdmin ? "opacity-30 cursor-not-allowed" : "hover:bg-mb-emerald/10 text-mb-emerald"}`}
                              title={isAdmin ? "Can't impersonate another admin" : "Log in as this user"}
                              data-testid={`admin-user-impersonate-${u.email}`}
                            >
                              {impersonatingId === u.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <LogIn className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              onClick={() => sendReset(u)}
                              disabled={resettingId === u.id}
                              className="p-1.5 rounded hover:bg-amber-50 text-amber-600 disabled:opacity-50"
                              title="Send password reset link"
                              data-testid={`admin-user-reset-${u.email}`}
                            >
                              {resettingId === u.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <KeyRound className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              onClick={() => syncFromStripe(u)}
                              disabled={syncingId === u.id}
                              className="p-1.5 rounded hover:bg-purple-50 text-purple-600 disabled:opacity-50"
                              title="Force-resync subscription from live Stripe (fixes 'paid but not subscribed')"
                              data-testid={`admin-user-stripe-sync-${u.email}`}
                            >
                              {syncingId === u.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <RefreshCw className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              onClick={() => {
                                setSelected(new Set([u.id]));
                                setComposeOpen(true);
                              }}
                              className="p-1.5 rounded hover:bg-mb-emerald/10 text-mb-emerald"
                              title="Email this user"
                              data-testid={`admin-user-email-${u.email}`}
                            >
                              <Mail className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setPendingDelete({ id: u.id, email: u.email })}
                              disabled={isAdmin}
                              className={`p-1.5 rounded ${isAdmin ? "opacity-30 cursor-not-allowed" : "hover:bg-red-50 text-red-600"}`}
                              title={isAdmin ? "Can't delete an admin" : "Hard delete"}
                              data-testid={`admin-user-delete-${u.email}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Compose modal */}
        {composeOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => !sending && setComposeOpen(false)}>
            <div className="bg-white rounded-lg max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-users-compose-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-xl">Email {selected.size} user{selected.size === 1 ? "" : "s"}</h3>
                <button onClick={() => setComposeOpen(false)} disabled={sending} className="p-1 rounded hover:bg-neutral-100">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-neutral-500 mb-3">
                Sent via the &quot;note from the desk&quot; template. Line-breaks become paragraphs.
              </p>
              <label className="label-uppercase text-xs">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Quick heads up on this week's screener…"
                className="mt-1 mb-4 rounded-md"
                data-testid="admin-users-compose-subject"
              />
              <label className="label-uppercase text-xs">Message</label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                placeholder="Hey — quick note from the desk. …"
                className="mt-1 rounded-md"
                data-testid="admin-users-compose-body"
              />
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setComposeOpen(false)} disabled={sending}>Cancel</Button>
                <Button onClick={sendBulk} disabled={sending} className="bg-mb-emerald hover:bg-mb-emerald-dark text-white" data-testid="admin-users-compose-send">
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send to {selected.size}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm */}
        {pendingDelete && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !deleting && setPendingDelete(null)}>
            <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-users-delete-modal">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-display font-bold text-lg">Hard-delete user?</h3>
                  <p className="text-sm text-neutral-600 mt-1">
                    <span className="font-mono-data">{pendingDelete.email}</span> will be permanently removed from Mongo, Resend audiences, and email queues. This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={deleting}>Cancel</Button>
                <Button onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-users-delete-confirm">
                  {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete permanently
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
